import {i18n} from '@online-offer/i18n';
import {
  Box,
  Center,
  getValueByDevice,
  Heading,
  Hide,
  HStack,
  Image,
  Show,
  Space,
  Text,
  useDevice,
  VStack,
} from 'platform/foundation';
import {useDateTimeFormatter, useLocale} from 'platform/locale';

import {useParams} from 'react-router-dom';

import {noop} from 'ramda-adjunct';

import {parseDate} from 'shared/web';

import {useLazyGetPdfByOfferIdQuery} from '../../api/onlineOfferApi';
import {APPLICATION_NAME} from '../../consts/applicationName';
import {useGetOnlineOfferData} from '../../hooks/useGetOnlineOfferData';
import {getWorkspaceCustomization} from '../../utils/getWorkspaceCustomization';
import {getWorkspaceFromUri} from '../../utils/getWorkspaceFromUri';
import {makeStorageResizeUrl} from '../../utils/makeStorageResizeUrl';
import {normalizeString} from '../../utils/normalizeString';
import {Button} from '../Button/Button';
import {ButtonIcon} from '../ButtonIcon/ButtonIcon';
import {FetchingButton} from '../FetchingButton/FetchingButton';

// we're multiplying by 2 for larger screens to have better quality
const LOGO_WIDTH = 220 * 2;
const LOGO_HEIGHT = 44 * 2;

const {workspace} = getWorkspaceFromUri();

export function Navigation() {
  const {logo: applicationLogo} = getWorkspaceCustomization();
  const {activeReservation, customer, vehicle, tenantLogoUrl, offerValidTo} =
    useGetOnlineOfferData();
  const params = useParams<{offerId: string}>();
  const locale = useLocale();
  const device = useDevice();
  const formatDateTime = useDateTimeFormatter();
  const [getPdfByOfferId, {isFetching, isSuccess, isError}] = useLazyGetPdfByOfferIdQuery();

  const customerName = activeReservation?.customer?.name ?? customer;
  const formattedOfferValidTo = offerValidTo
    ? formatDateTime('dateShort', parseDate(offerValidTo))
    : null;

  const resizedLogoUrl = tenantLogoUrl
    ? makeStorageResizeUrl(tenantLogoUrl, {
        width: LOGO_WIDTH,
        height: LOGO_HEIGHT,
        withoutEnlargement: true,
      })
    : undefined;

  const downloadPdf = () => {
    if (!params.offerId || !workspace) {
      return;
    }

    getPdfByOfferId({
      offerId: params.offerId,
      workspace,
      locale: locale.language,
      filename: vehicle?.title ? normalizeString(vehicle.title) : 'online-offer',
    })
      .unwrap()
      .then((response) => {
        const anchor = document.createElement('a');
        anchor.href = response.uri;
        document.body.appendChild(anchor);
        anchor.click();
        document.body.removeChild(anchor);
      })
      .catch(noop);
  };

  const onShare = () =>
    navigator.share({
      title: `${vehicle?.title} • ${APPLICATION_NAME}`,
      url: window.location.href,
    });

  return (
    <>
      <Box
        position={getValueByDevice(device, 'sticky', 'static', 'static', 'static')}
        top={0}
        backgroundColor="general.white"
        zIndex="MENU_BAR"
        data-testid="navigationContainer"
      >
        <Center justify="center" width="100%">
          <Box
            flexGrow={1}
            maxWidth={432}
            paddingTop={[2, 4, 4, 6]}
            paddingBottom={[0, 4, 4, 6]}
            paddingHorizontal={[3, 8, 8, 20]}
          >
            <HStack align="center" justify="space-between">
              <Box>
                {resizedLogoUrl ? <Image maxHeight={11} src={resizedLogoUrl} /> : applicationLogo}
              </Box>
              <Hide onMobile>
                <Box paddingLeft={8}>
                  <VStack>
                    <Text size="small" color="secondary">
                      {i18n.t('navigation.offerFor', {
                        code: activeReservation?.businessCase?.code ?? '',
                      })}
                    </Text>
                    <Heading size={4}>{customerName}</Heading>
                  </VStack>
                </Box>
              </Hide>
              <Space horizontal={4} fillAvailable />
              <HStack spacing={2}>
                <Show onMobile>
                  <ButtonIcon
                    data-testid="downloadButton"
                    variant="outline"
                    icon="download"
                    onClick={downloadPdf}
                    size="small"
                  />
                </Show>
                <Hide onMobile>
                  <FetchingButton
                    data-testid="downloadButton"
                    variant="outline"
                    title={i18n.t('downloadPdf')}
                    icon="download"
                    isSuccess={isSuccess}
                    isFetching={isFetching}
                    isError={isError}
                    onClick={downloadPdf}
                  />
                </Hide>
                <Show when={navigator.share}>
                  <Show onMobile>
                    <ButtonIcon
                      data-testid="shareButton"
                      variant="outline"
                      icon="share"
                      onClick={onShare}
                      size="small"
                    />
                  </Show>
                  <Hide onMobile>
                    <Button
                      data-testid="shareButton"
                      variant="outline"
                      title={i18n.t('share')}
                      leftIcon="share"
                      onClick={onShare}
                    />
                  </Hide>
                </Show>
              </HStack>
            </HStack>
            <Show onMobile>
              <Box paddingTop={3}>
                <Show onMobile>
                  <VStack>
                    <Text size="small" color="secondary">
                      {i18n.t('navigation.offerFor', {
                        code: activeReservation?.businessCase?.code ?? '',
                      })}
                    </Text>
                    <Heading size={4}>{customerName}</Heading>
                    {formattedOfferValidTo && (
                      <Text size="small" color="secondary">
                        {i18n.t('navigation.offerValidTo')} {formattedOfferValidTo}
                      </Text>
                    )}
                  </VStack>
                </Show>
              </Box>
            </Show>
          </Box>
        </Center>
      </Box>
    </>
  );
}
